import { useCallback, useEffect, useState } from 'react';

import type { CampaignFolderResponse, LibraryFolder, LibraryFoldersResponse } from '@nexus/shared';

import { ApiError, apiGet, apiSend } from '../lib/api';
import { Alert, Button, Card, SelectField } from '../ui';

/**
 * The operator's folder picker: which clip-library folder this campaign hands
 * its creators.
 *
 * The choices come from the service's own allowlist (`folders.json` on
 * supermicro), read through the Worker with a signed address — the browser
 * never holds the signing secret and never talks to the service. Saving an id
 * the service does not know is refused by the Worker, so a typo cannot leave a
 * campaign pointing at nothing.
 *
 * Self-contained, like the creator-side section: it fetches its own two
 * facts and the admin campaign page it sits on does not know it exists.
 */
export function ClipFolderPicker({ idOrSlug }: { idOrSlug: string }): React.JSX.Element {
  const [current, setCurrent] = useState<string | null | undefined>(undefined);
  const [folders, setFolders] = useState<LibraryFolder[] | 'offline' | undefined>(undefined);
  const [choice, setChoice] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [link, list] = await Promise.all([
        apiGet<CampaignFolderResponse>(`/admin/library/campaigns/${encodeURIComponent(idOrSlug)}/folder`),
        apiGet<LibraryFoldersResponse>('/admin/library/folders'),
      ]);
      setCurrent(link.folderId);
      setChoice(link.folderId ?? '');
      setFolders(list.status === 'ok' ? list.folders : 'offline');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the clip library.');
      setCurrent(null);
      setFolders('offline');
    }
  }, [idOrSlug]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const body = await apiSend<CampaignFolderResponse>(
        'PATCH',
        `/admin/library/campaigns/${encodeURIComponent(idOrSlug)}/folder`,
        { folderId: choice === '' ? null : choice },
      );
      setCurrent(body.folderId);
      setNotice(
        body.folderId
          ? `Creators on this campaign now see the clips in “${body.folderId}”.`
          : 'This campaign no longer offers ready-made clips.',
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the folder.');
    } finally {
      setBusy(false);
    }
  }

  const offline = folders === 'offline';
  const options = folders === 'offline' || folders === undefined ? [] : folders;
  /* The linked folder is always offered, even if the service is down or has
     since dropped it, so the select cannot silently show the wrong value. */
  const known = new Set(options.map((f) => f.id));
  const stale = current && !known.has(current) ? current : null;

  return (
    <Card
      title="Clips to download"
      sub="The Forge output folder on the NAS whose clips creators on this campaign can download. Add folders in /opt/clip-library/folders.json on supermicro; pick one here."
    >
      {offline && (
        <Alert tone="waiting" title="The clip library is not answering">
          <span>
            The folder list cannot be read and a change cannot be checked, so saving is off until it is
            back. Creators see “{`The clip library is offline right now`}” on the campaign page meanwhile.
          </span>
        </Alert>
      )}
      <form onSubmit={save} className="form">
        <SelectField
          label="Folder"
          name="clipFolderId"
          value={choice}
          onChange={(event) => setChoice(event.target.value)}
          disabled={offline || folders === undefined}
          hint={
            current
              ? `Linked to “${current}”.`
              : 'Not linked: the campaign page shows no clips section.'
          }
        >
          <option value="">— No ready-made clips —</option>
          {stale && (
            <option value={stale}>
              {stale} (not on the allowlist any more)
            </option>
          )}
          {options.map((folder) => (
            <option key={folder.id} value={folder.id}>
              {folder.id} — {folder.files === null ? 'unreadable' : `${folder.files} clip${folder.files === 1 ? '' : 's'}`} — {folder.path}
            </option>
          ))}
        </SelectField>
        <div className="actions">
          <Button type="submit" variant="primary" disabled={busy || offline || folders === undefined || choice === (current ?? '')}>
            {busy ? 'Saving…' : 'Save folder'}
          </Button>
        </div>
      </form>
      {notice && <Alert tone="positive">{notice}</Alert>}
      {error && <Alert tone="critical">{error}</Alert>}
    </Card>
  );
}
